import { Badge, Stack, Button } from "react-bootstrap"
import { useUserStore } from "../../api/users/store"
import useUXStore from "../../api/ux/store"
import CAccordion from "../../components/CAccordion"
import CStack from "../../components/CStack"
import Label from "../../components/Label"
import Loading from "../../components/Loading"
import ConfirmationInput from "../../components/ConfirmationInput"
import { useEffect } from "react"
import { ReactComponent as IosIcon } from "../../svgs/ios.svg"
import { ReactComponent as AndroidIcon } from "../../svgs/android.svg"

const DeviceList = () => {
    const { devices, retrieveDevices, removeDevice, updateDeviceStatus, currentUser, loading } = useUserStore(state => state)
    const { refreshDialog, addToast } = useUXStore(state => state)

    useEffect(() => {
        if (currentUser?.ClientId) {
            retrieveDevices()
        }
    }, [currentUser?.ClientId])

    const getStatusBadgeVariant = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active': return 'success'
            case 'blocked': return 'danger'
            case 'pending': return 'warning'
            default: return 'secondary'
        }
    }

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleString()
        } catch {
            return dateStr
        }
    }

    const handleRemoveDevice = (device: any) => {
        const confirmText = "REMOVE"

        refreshDialog({
            id: "deviceRemove",
            title: "Remove Device",
            visible: true,
            confirmActive: false,
            confirmContent: "Remove Device",
            content: (
                <ConfirmationInput
                    message={`Are you sure you want to remove device "${device.name}"?`}
                    warningMessage="This action will permanently remove this device. The user will need to re-register this device to use it again."
                    confirmText={confirmText}
                    onValidChange={(isValid) => {
                        refreshDialog({
                            id: "deviceRemove",
                            confirmActive: isValid
                        })
                    }}
                />
            ),
            onConfirm: () => {
                removeDevice(device.id)
                    .then(() => {
                        addToast("Device Removed", `Device "${device.name}" has been removed successfully.`)
                    })
                    .catch((err: any) => {
                        addToast("Remove Failed", "Failed to remove device: " + err.message)
                    })
            },
        }, true)
    }

    const handleToggleStatus = (device: any) => {
        const newStatus = device.status === 'blocked' ? 'active' : 'blocked'
        const action = device.status === 'blocked' ? 'unblock' : 'block'
        const confirmText = action.toUpperCase()
        const warningMessage = device.status === 'blocked'
            ? "This action will allow this device to access the system again."
            : "This action will prevent this device from accessing the system."

        refreshDialog({
            id: "deviceStatus",
            title: `${action.charAt(0).toUpperCase() + action.slice(1)} Device`,
            visible: true,
            confirmActive: false,
            confirmContent: `${action.charAt(0).toUpperCase() + action.slice(1)} Device`,
            content: (
                <ConfirmationInput
                    message={`Are you sure you want to ${action} device "${device.name}"?`}
                    warningMessage={warningMessage}
                    confirmText={confirmText}
                    onValidChange={(isValid) => {
                        refreshDialog({
                            id: "deviceStatus",
                            confirmActive: isValid
                        })
                    }}
                />
            ),
            onConfirm: () => {
                updateDeviceStatus(device.id, newStatus)
                    .then(() => {
                        addToast("Status Updated", `Device "${device.name}" has been ${action}ed successfully.`)
                    })
                    .catch((err: any) => {
                        addToast("Update Failed", "Failed to update device status: " + err.message)
                    })
            },
        }, true)
    }

    if (loading && !devices) {
        return (
            <CStack header="Devices">
                <Loading expandHeight />
            </CStack>
        )
    }

    const deviceCount = devices?.length || 0
    const header = deviceCount > 0 ? `Devices (${deviceCount})` : "Devices"

    return (
        <CStack header={header}>
            {!devices || devices.length === 0 ? (
                <Label>No devices found</Label>
            ) : (
                devices.map((device, index) => {
                    const deviceType = device.type?.toLowerCase()
                    const isIOS = deviceType === 'ios'
                    const isAndroid = deviceType === 'android'
                    const deviceIcon = isIOS ? IosIcon : isAndroid ? AndroidIcon : undefined

                    return (
                    <CAccordion
                        key={device.id || index}
                        label={device.name || 'Unknown Device'}
                        icon={deviceIcon}
                    >
                        <Stack gap={2} style={{ padding: '10px' }}>
                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">Status:</Label>
                                <Badge bg={getStatusBadgeVariant(device.status)}>
                                    {device.status}
                                </Badge>
                            </Stack>

                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">Type:</Label>
                                <Label size="small" textAlign="right">
                                    {device.type || 'Unknown'}
                                </Label>
                            </Stack>

                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">Last Accessed:</Label>
                                <Label size="small" textAlign="right">
                                    {formatDate(device.lastAccessed)}
                                </Label>
                            </Stack>

                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">User ID:</Label>
                                <Label size="small" textAlign="right">
                                    {device.userId}
                                </Label>
                            </Stack>

                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">Passkey:</Label>
                                <Label size="small" textAlign="right">
                                    {device.authCapabilities?.hasPasskey ? 'Yes' : 'No'}
                                </Label>
                            </Stack>

                            <Stack direction="horizontal" className="justify-content-between">
                                <Label size="small" color="gray">Biometric:</Label>
                                <Label size="small" textAlign="right">
                                    {device.authCapabilities?.hasBiometric ? 'Yes' : 'No'}
                                </Label>
                            </Stack>

                            <Stack direction="horizontal" gap={2} className="mt-3">
                                <Button
                                    size="sm"
                                    variant={device.status === 'blocked' ? 'outline-success' : 'outline-warning'}
                                    onClick={() => handleToggleStatus(device)}
                                >
                                    {device.status === 'blocked' ? 'Unblock' : 'Block'}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleRemoveDevice(device)}
                                >
                                    Remove
                                </Button>
                            </Stack>
                        </Stack>
                    </CAccordion>
                    )
                })
            )}
        </CStack>
    )
}

export default DeviceList